import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AuthUser } from "@app/auth/model/auth-user";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { EntityInfoService } from "@app/entity/services/entityinfo.service";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { UserManagementService } from "@app/user/service/user-management.service";
import { Observable, Subject, combineLatest, filter, map, shareReplay, switchMap, take, tap } from "rxjs";

@Component({
    selector: "app-company-details-step",
    templateUrl: "./company-details-step.component.html",
    styleUrls: ["./company-details-step.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDetailsStepComponent implements OnInit, OnDestroy, OnChanges {
    @Input() preservedEntity!: number;
    @Input() isReadOnly = false;
    @Output() completed = new EventEmitter<number>();
    public authUser$: Observable<AuthUser>;
    public entities$: Observable<Entity[]>;
    public form: FormGroup = this.formBuilder.group({ entityId: [null, Validators.required] });

    private selectedEntity: Entity;
    private unsubscribe$ = new Subject();

    constructor(
        public readonly errorMatcher: AppErrorStateMatcher,
        private readonly projectDispatcher: ProjectDispatchers,
        private readonly entityInfoService: EntityInfoService,
        private readonly userService: UserManagementService,
        private readonly entityService: EntityService,
        private readonly formBuilder: FormBuilder
    ) {}
    ngOnChanges(changes: NgChanges<CompanyDetailsStepComponent>): void {
        if (changes.isReadOnly?.currentValue) {
            this.form.disable();
        }
    }

    ngOnInit() {
        this.authUser$ = this.projectDispatcher.getAuthUser().pipe(shareReplay(1));
        this.entities$ = this.authUser$.pipe(
            tap(authUser => {
                if (authUser) {
                    this.projectDispatcher.loadEntites(authUser.entityId);
                    this.entityInfoService.dispatchEntityUsers(authUser.entityId);
                    const entityId = this.preservedEntity || authUser.entityId;
                    this.form.patchValue({
                        entityId,
                    });
                    this.userService.dispatchGetUserRoles(authUser);
                }
            }),
            filter(authUser => !!authUser),
            switchMap(authUser =>
                combineLatest([this.entityService.selectEntity$(authUser.entityId), this.entityService.selectEntitiesArray$()]).pipe(
                    filter(([entity, entities]) => !!(entity && entities)),
                    take(1),
                    map(([entity, entities]) => this.getSubDealers(entities, entity, []))
                )
            ),
            shareReplay(1)
        );

        this.form.controls.entityId.valueChanges.subscribe(value => {
            this.setSelectedEntity(value);
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    public isValid(): boolean {
        return this.isReadOnly || this.form.valid;
    }

    public getValue(): string {
        return this.form.controls.entityId.value;
    }

    public getSelectedEntity(): Entity {
        return this.selectedEntity;
    }

    private setSelectedEntity(entityId: number): void {
        this.entities$.subscribe(entities => {
            this.selectedEntity = entities.find(entity => entity.id === +entityId);
        });
    }

    private getSubDealers(entityArray: Entity[], parent: Entity, subDealers: Entity[]): Entity[] {
        subDealers.push(parent);
        for (const entity of entityArray) {
            if (entity.parentId === parent.id) {
                this.getSubDealers(entityArray, entity, subDealers);
            }
        }
        return subDealers.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
}
